import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/constants";
import { INFO_PAGE, FOOTER } from "@/constants/testIds";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const Shell = ({ ns, id, children, showUpdated }) => {
  const { t } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div
      data-testid={`${INFO_PAGE.root}-${id}`}
      className="max-w-3xl mx-auto px-4 py-8"
    >
      <Link
        to="/"
        data-testid={INFO_PAGE.back}
        className="inline-flex items-center gap-1 text-sm font-medium text-[#123A2C] mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {t("footer.backHome")}
      </Link>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#14251F]">
        {t(`${ns}.title`)}
      </h1>
      <p className="mt-3 text-[#4B5563] leading-relaxed">{t(`${ns}.intro`)}</p>
      {showUpdated && (
        <p className="mt-1 text-xs text-[#6B7280]">{t("footer.lastUpdated")}</p>
      )}
      {children}
    </div>
  );
};

const Sections = ({ ns }) => {
  const { t } = useLang();
  const items = t(`${ns}.sections`);
  return (
    <div className="mt-6 space-y-3">
      {items.map((s, i) => (
        <section
          key={i}
          className="bg-white rounded-xl border border-[#E5E7EB] p-5"
        >
          <h2 className="font-bold text-[#14251F]">
            {i + 1}. {s.h}
          </h2>
          <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.p}</p>
        </section>
      ))}
    </div>
  );
};

export const About = () => (
  <Shell ns="about" id="about">
    <Sections ns="about" />
  </Shell>
);

export const Privacy = () => (
  <Shell ns="privacy" id="privacy" showUpdated>
    <Sections ns="privacy" />
  </Shell>
);

export const Terms = () => (
  <Shell ns="terms" id="terms" showUpdated>
    <Sections ns="terms" />
  </Shell>
);

export const Faq = () => {
  const { t } = useLang();
  const items = t("faq.items");
  return (
    <Shell ns="faq" id="faq">
      <Accordion
        type="single"
        collapsible
        className="mt-6 bg-white rounded-xl border border-[#E5E7EB] px-5"
      >
        {items.map((it, i) => (
          <AccordionItem key={i} value={`q${i}`}>
            <AccordionTrigger className="text-left text-[#14251F]">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#4B5563] leading-relaxed">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <p className="mt-6 text-sm text-[#4B5563]">
        {t("faq.stillNeed")}{" "}
        <Link to="/contact" className="font-medium text-[#123A2C] underline">
          {t("faq.contactUs")}
        </Link>
      </p>
    </Shell>
  );
};

export const Contact = () => {
  const { t } = useLang();
  const row =
    "flex items-center gap-3 bg-white rounded-xl border border-[#E5E7EB] p-4 text-[#14251F] hover:border-[#123A2C] transition-colors";
  return (
    <Shell ns="contact" id="contact">
      <div className="mt-6 space-y-3">
        {CONTACT.email && (
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid={FOOTER.email}
            className={row}
          >
            <Mail className="w-5 h-5 text-[#123A2C]" />
            <span>
              <span className="block text-xs text-[#6B7280]">
                {t("contact.emailLabel")}
              </span>
              {CONTACT.email}
            </span>
          </a>
        )}
        {CONTACT.phone && (
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid={FOOTER.phone}
            className={row}
          >
            <Phone className="w-5 h-5 text-[#123A2C]" />
            <span>
              <span className="block text-xs text-[#6B7280]">
                {t("contact.phoneLabel")}
              </span>
              {CONTACT.phone}
            </span>
          </a>
        )}
        <p className="text-xs text-[#6B7280]">{t("contact.hours")}</p>
      </div>
    </Shell>
  );
};
